import { defineRecipe } from '@chakra-ui/react';
import { Borders, Colors, Shadows, Space } from '../../types';

// The primary single-part `input` recipe; per-size dimensions are plain `--input-*` custom properties. Its
// base, sizes and variants are exported for Textarea, Select (NativeSelect) and NumberInput.

const invalidBase = {
  borderColor: Colors.SURFACE_ERROR,
  boxShadow: '0 0 0 2px var(--udl-colors-surface-error)',
  outline: '2px solid',
  outlineColor: Colors.SURFACE_ERROR,
  outlineOffset: '-1px',
  _hover: { borderColor: Colors.SURFACE_ERROR },
};

const invalidStyles = {
  borderColor: Colors.SURFACE_ERROR,
  _hover: { borderColor: Colors.SURFACE_ERROR },
};

const invalidFlushedStyles = {
  borderColor: Colors.SURFACE_ERROR,
  boxShadow: 'inset 0 -1px 0 0px var(--udl-colors-surface-error)',
};

const focusFlushedStyles = {
  bg: Colors.SURFACE_PAGE,
  borderColor: Colors.BORDER_FOCUS,
  boxShadow: Shadows.OUTLINE,
  _hover: { borderColor: Colors.BORDER_EMPHASIS },
};

const focusStyles = {
  bg: Colors.SURFACE_PAGE,
  borderColor: Colors.BORDER_FOCUS,
  boxShadow: 'none',
  color: Colors.TEXT_EMPHASIS,
  outline: '2px solid',
  outlineColor: Colors.BORDER_FOCUS,
  outlineOffset: '-1px',
};

const readOnlyStyles = {
  boxShadow: 'none !important',
  color: Colors.TEXT_MUTED,
  bg: Colors.SURFACE_PAGE,
  userSelect: 'all',
  cursor: 'not-allowed',
  borderColor: Colors.BORDER_DEFAULT,
  _hover: { borderColor: Colors.BORDER_DEFAULT },
};

export const inputFieldBase = {
  '&::-webkit-search-cancel-button': { display: 'none' },
  width: '100%',
  height: 'var(--input-height)',
  fontSize: 'var(--input-font-size)',
  px: 'var(--input-padding)',
  borderRadius: 'var(--input-border-radius)',
  minWidth: 0,
  minW: 'var(--input-height)',
  outline: 0,
  position: 'relative',
  appearance: 'none',
  textAlign: 'start',
  bg: Colors.SURFACE_PAGE,
  color: Colors.TEXT_EMPHASIS,
  _readOnly: readOnlyStyles,
  _invalid: invalidBase,
  _disabled: {
    opacity: 1,
    cursor: 'not-allowed',
    borderColor: Colors.SURFACE_DISABLED,
    bg: Colors.SURFACE_DISABLED,
    color: Colors.TEXT_DISABLED,
    _hover: {
      borderColor: Colors.SURFACE_DISABLED,
      bg: Colors.SURFACE_DISABLED,
      color: Colors.TEXT_DISABLED,
    },
  },
};

// per-size dimensions
export const inputFieldSizes: Record<string, any> = {
  lg: {
    '--input-font-size': 'fontSizes.lg',
    '--input-padding': 'spacing.4',
    '--input-border-radius': 'radii.xl',
    '--input-height': 'sizes.12',
  },
  md: {
    '--input-font-size': 'fontSizes.sm',
    '--input-padding': 'spacing.3',
    '--input-border-radius': 'radii.lg',
    '--input-height': 'sizes.10',
  },
  sm: {
    '--input-font-size': 'fontSizes.sm',
    '--input-padding': 'spacing.3',
    '--input-border-radius': 'radii.md',
    '--input-height': 'sizes.8',
  },
  xs: {
    '--input-font-size': 'fontSizes.xs',
    '--input-padding': 'spacing.2',
    '--input-border-radius': 'radii.base',
    '--input-height': 'sizes.7',
  },
};
inputFieldSizes.base = inputFieldSizes.md;
inputFieldSizes.compact = inputFieldSizes.sm;

export const inputFieldVariants: Record<string, any> = {
  outline: {
    borderWidth: Borders['1PX'],
    borderStyle: 'solid',
    borderColor: Colors.BORDER_INPUT,
    bg: Colors.SURFACE_PAGE,
    _hover: { borderColor: Colors.BORDER_EMPHASIS },
    _invalid: invalidStyles,
    _focusVisible: focusStyles,
  },
  filled: {
    border: Borders['1PX'],
    bg: Colors.SURFACE_TERTIARY,
    _hover: {
      borderColor: Colors.TRANSPARENT,
      bg: Colors.SURFACE_TERTIARY_HOVER,
      color: Colors.TEXT_EMPHASIS,
    },
    _active: { color: Colors.TEXT_EMPHASIS, bg: Colors.SURFACE_PAGE },
    _readOnly: readOnlyStyles,
    _invalid: invalidStyles,
    _focusVisible: focusStyles,
  },
  flushed: {
    borderBottomWidth: Borders['2PX'],
    borderBottomStyle: 'solid',
    borderColor: Colors.BORDER_MUTED,
    borderBottomRadius: '0',
    px: Space['2XS'],
    bg: Colors.TRANSPARENT,
    _hover: { bg: Colors.SURFACE_TERTIARY, borderColor: Colors.BORDER_EMPHASIS },
    _readOnly: readOnlyStyles,
    _invalid: invalidFlushedStyles,
    _focusVisible: focusFlushedStyles,
  },
  unstyled: {
    bg: Colors.TRANSPARENT,
    px: '0',
  },
};

export const inputRecipe = defineRecipe({
  base: inputFieldBase,
  variants: {
    variant: inputFieldVariants,
    size: inputFieldSizes,
  },
  defaultVariants: {
    size: 'md',
    variant: 'outline',
  },
});
